import {useNavigate, useParams} from 'react-router-dom';
import {Box, Button, InputAdornment, MenuItem, Typography, useTheme} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import {
  InvalidationRuleFieldOptionHotels,
  InvalidationRuleOperator,
  InvalidationRuleServiceTypes,
} from '@bandido/shared/types';

import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledSwitch } from '@frontend/features/common/components/ControlledSwitch';
import { yupResolver } from '@hookform/resolvers/yup';
import {useEffect, useRef, useState} from 'react';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { LoadingButton } from '@mui/lab';
import { TFunction } from 'i18next';
import { useGetInvalidationRuleById } from '@frontend/features/invalidation-rules/queries';
import {routes} from '@frontend/router/routes';
import dayjs from 'dayjs';
import {CONDITIONS_FIELD, CREATE_INVALIDATION_RULE} from '@frontend/features/invalidation-rules/constants';

interface InvalidationRulesFormValues {
  name: string;
  isActive: boolean;
  isDefault?: boolean;
  expireAfter: number;
  conditions?: {
    field: NonNullable<InvalidationRuleFieldOptionHotels | undefined>;
    operator: NonNullable<InvalidationRuleOperator | undefined>;
    value: string;
  }[];
}

export const invalidationRulesSchema = (t: TFunction<'translation', undefined>) => Yup.object({
  name: Yup.string()
    .required(t('invalidationRules.validations.fieldRequired'))
    .max(255, t('invalidationRules.validations.nameCharLength')),
  expireAfter: Yup.number()
    .typeError(t('invalidationRules.validations.expireAfterMustBeNumber'))
    .required(t('invalidationRules.validations.fieldRequired'))
    .min(0,t('invalidationRules.validations.expireAfterMin')),
  isActive: Yup.boolean()
    .required(t('invalidationRules.validations.fieldRequired')),
  isDefault: Yup.boolean().optional(),
  conditions: Yup.array()
    .of(
      Yup.object({
        field: Yup.mixed<InvalidationRuleFieldOptionHotels>()
          .required(t('invalidationRules.validations.fieldRequired'))
          .oneOf(
            Object.values(InvalidationRuleFieldOptionHotels),
            t('invalidationRules.validations.fieldInvalid')
          ),
        operator: Yup.mixed<InvalidationRuleOperator>()
          .required(t('invalidationRules.validations.fieldRequired'))
          .oneOf(
            Object.values(InvalidationRuleOperator),
            t('invalidationRules.validations.operatorInvalid')
          ),
        value: Yup.string()
          .required(t('invalidationRules.validations.fieldRequired'))
          .when(['field'], (fields, schema) => {
            const [field] = fields;

            if (
              field === InvalidationRuleFieldOptionHotels.CHECK_IN_DATE ||
              field === InvalidationRuleFieldOptionHotels.CHECK_OUT_DATE
            ) {
              return schema
                .required(t('invalidationRules.validations.fieldRequired'))
                .test(
                  'valid-date',
                  t('invalidationRules.validations.valueInvalidDate'),
                  (value) => !!value && dayjs(value, 'YYYY-MM-DD', true).isValid()
                );
            }

            return schema
              .required(t('invalidationRules.validations.fieldRequired'))
              .max(255, t('invalidationRules.validations.valueCharLength'));
          })
      })
    )
    .when('isDefault', {
      is: false,
      then: (schema) => schema.min(1, t('invalidationRules.validations.minConditions')).required(),
      otherwise: (schema) => schema.optional()
    })
});

export const InvalidationRulesView = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { invalidationRuleId, id: agencyId } = useParams();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const navigate = useNavigate();

  const methods = useForm<InvalidationRulesFormValues>({
    resolver: yupResolver(invalidationRulesSchema(t)),
    defaultValues: {
      name: '',
      expireAfter:60,
      isActive: true,
      isDefault: false,
      conditions: []
    },
  });

  const [isDefault, setIsDefault] = useState(methods.getValues('isDefault'));
  useEffect(() => {
    const subscription = methods.watch((value) => {
      setIsDefault(value.isDefault ?? false);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const { data: dbRule } = useGetInvalidationRuleById({
    id: String(invalidationRuleId)
  });
  const { handleSubmit, control, formState: { errors } } = methods;

  const hasInsertedInitialCondition = useRef(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: CONDITIONS_FIELD,
  });
  
  const handleGoToList = () => navigate(
    `${routes.dashboard.agencyView.root.replace(':id', String(agencyId))}/${routes.dashboard.agencyView.invalidationRules}`
  );

  const onSubmit = async (data: InvalidationRulesFormValues) => {
    const isCreate = invalidationRuleId === CREATE_INVALIDATION_RULE;
    const requestBody = {
      name: data.name,
      serviceType: InvalidationRuleServiceTypes.HOTELS,
      isActive: data.isActive,
      expireAfter: data.expireAfter,
      agencyId: Number(agencyId),
      conditions: data.conditions||[]
    };

    setLoading(true);
    try {

      if (invalidationRuleId === CREATE_INVALIDATION_RULE) {
        await client.post('/invalidation-rules', requestBody);
      } else {
        await client.patch(`/invalidation-rules/${invalidationRuleId}`, requestBody);
      }
          
      addToast({
        message: t(`invalidationRules.viewEdit.${isCreate?'createSuccess':'updateSuccess'}`),
        color: 'success',
      });
      setTimeout(handleGoToList, 2000);
    } catch (e) {
      addToast({
        message: t(`invalidationRules.viewEdit.${isCreate?'createError':'updateError'}`),
        color: 'error',
      });
    
    } finally {
      setLoading(false);
    }

  };

  const handleAddConditionBlock = () => append({ field: InvalidationRuleFieldOptionHotels.SUPPLIER_ACCESS_CODE, operator: InvalidationRuleOperator.EQUALS, value: '' });

  const invalidationRuleFieldOptions = Object.entries(InvalidationRuleFieldOptionHotels).map(([key, value]) => ({
    value,
    label: t(`invalidationRules.fieldOptions.${key}`)
  }));

  const operatorFieldOptions = Object.entries(InvalidationRuleOperator).map(([key, value]) => ({
    value,
    label: t(`invalidationRules.operatorOptions.${key}`)
  }));

  useEffect(() => {
    if (invalidationRuleId && invalidationRuleId !== CREATE_INVALIDATION_RULE && dbRule) {
      methods.reset({
        name: dbRule.name,
        isActive: dbRule.isActive,
        isDefault: dbRule.isDefault,
        expireAfter: dbRule.expireAfter,
        conditions: dbRule.conditions.map((condition) => ({
          field: condition.field as NonNullable<InvalidationRuleFieldOptionHotels>,
          operator: condition.operator as NonNullable<InvalidationRuleOperator>,
          value: condition.value,
        }))
      });
    }
  }, [invalidationRuleId, dbRule, methods]);


  useEffect(() => {
    if (
      invalidationRuleId === CREATE_INVALIDATION_RULE &&
        !hasInsertedInitialCondition.current
    ) {
      const currentFields = methods.getValues(CONDITIONS_FIELD);
      if (!currentFields || currentFields.length === 0) {
        handleAddConditionBlock();
        hasInsertedInitialCondition.current = true;
      }
    }
  }, [invalidationRuleId]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ListLayout
          padding={0}
          title={invalidationRuleId === CREATE_INVALIDATION_RULE ? t('invalidationRules.viewEdit.titleNew') : t('invalidationRules.viewEdit.titleEdit')}
          subtitle={invalidationRuleId === CREATE_INVALIDATION_RULE ? t('invalidationRules.viewEdit.subtitleNew') : t('invalidationRules.viewEdit.subtitleEdit')}
        >
          <Box display="flex" flexDirection="row" alignItems="center" gap={2} width="100%" marginBottom={isDefault?theme.spacing(3):0}>
            <Box flex={4}>
              <ControlledTextField
                label={t('invalidationRules.name')}
                control={control}
                required
                name="name"
                placeholder={t('invalidationRules.name')}
                fullWidth
              />
            </Box>

            <Box flex={1}>
              <ControlledTextField
                label={t('invalidationRules.expireAfter')}
                control={control}
                type="number"
                required
                name="expireAfter"
                placeholder={t('invalidationRules.expireAfter')}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('invalidationRules.minutes')}</InputAdornment>,
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
              <ControlledSwitch control={control} name="isActive" label={t('invalidationRules.isActive')}/>
            </Box>
          </Box>

          {!isDefault && <Box
            display="flex"
            padding={theme.spacing(3)}
            flexDirection="column"
            alignItems="flex-start"
            borderRadius={'8px'}
            border={`1px solid ${theme.palette.divider}`}
            margin={theme.spacing(3, 0)}
            gap={3}
          >

            <Box width="100%">
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%"  marginBottom={theme.spacing(3)}>
                <Typography variant="h6">{t('invalidationRules.viewEdit.conditions')} *</Typography>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="baseline" gap={4}>
                {fields.map((_f, index) => (
                  <Box key={index} display="flex" justifyContent="flex-start" width="100%">
                    <Box display="flex" alignItems="center" width="100%" gap={2}>
                      <ControlledSelect  label={t('invalidationRules.viewEdit.field')} fullWidth size="small" control={control}  name={`conditions.${index}.field`}  required>
                        {invalidationRuleFieldOptions.map((optionItem, index) => (
                          <MenuItem key={index} value={optionItem.value}>
                            {optionItem.label}
                          </MenuItem>
                        ))}
                      </ControlledSelect>
                      <ControlledSelect label={t('invalidationRules.viewEdit.operator')} control={control} name={`conditions.${index}.operator`} size="small" fullWidth required>
                        {operatorFieldOptions.map((optionItem, index) => (
                          <MenuItem key={index} value={optionItem.value}>
                            {optionItem.label}
                          </MenuItem>
                        ))}
                      </ControlledSelect>
                      <ControlledTextField  label={t('invalidationRules.viewEdit.value')}  name={`conditions.${index}.value`} size="small" control={control} fullWidth />
                      <Button onClick={() => remove(index)} variant="text" style={{ padding: theme.spacing(0) }} startIcon={<Delete color="error" />} />
                    </Box>
                  </Box>
                ))}
              </Box>
              {errors.conditions && typeof errors.conditions.message === 'string' && (
                <Typography color="error" variant="caption">
                  {errors.conditions.message}
                </Typography>
              )}


            </Box>


            <Button size="large" sx={{ color: theme.palette.primary.main }} onClick={handleAddConditionBlock}>
              {t('invalidationRules.viewEdit.addCondition')}
            </Button>
          </Box>}

          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" color="error" sx={{ marginRight: theme.spacing(1) }} onClick={handleGoToList}>
              {t('invalidationRules.viewEdit.buttonDiscard')}
            </Button>
            <LoadingButton loading={loading} type="submit" variant="contained" color="primary">
              {t('common.save')}
            </LoadingButton>
          </Box>
        </ListLayout>
      </form>
    </FormProvider>
  );
};
